import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import { style } from 'typestyle';
import { Messages } from '../../../../../codegen/Localize';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { textFieldWidget } from '../../../../../userSettings/styles/EmailAndDisplayNameFormStyles';

export interface OAuthClientSetting {
  dbClass: string;
  instanceUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthClientRedirectUri: string;
  id: string;
}

export type OAuthClientsFormField = { oauthClients: OAuthClientSetting[] }
export type OAuthClientsFormikProps = FormikProps<OAuthClientsFormField>;

export interface AddOAuthClientDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  addOAuthClientSubmit: (values: AddOAuthClientDialogState, formik: OAuthClientsFormikProps) => void;
  formik: OAuthClientsFormikProps;
}

export interface AddOAuthClientDialogState {
  dbClass: string;
  instanceUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthClientRedirectUri: string;
}

const buttonStyle = style({
  marginTop: TabStyles.Spacing.SmallSpace
});

function validateUrl(value: string): boolean {
  // revise the regex pattern from https://www.tutorialspoint.com/How-to-validate-URL-address-in-JavaScript
  const urlPattern = new RegExp('^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{1,}|localhost|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
  return !!urlPattern.test(value);
}

export class AddOAuthClientDialog extends React.Component<AddOAuthClientDialogProps, AddOAuthClientDialogState> {
  constructor(props: AddOAuthClientDialogProps) {
    super(props);
  }

  validateNoEmbeddedWhiteSpace(value: string) {
    if (!value || !value.trim() || value.trim().indexOf(' ') > -1) {
      return Messages.invalidValue();
    }
  }

  validateInstanceUrl(value: string) {
    if (value && value.trim() && !validateUrl(value.trim())) {
      return Messages.invalidUrl();
    }
  }

  validateRedirectUri(value: string) {
    if (!value || !validateUrl(value.trim())) {
      return Messages.invalidUrl();
    }
  }

  handleSubmit(client: OAuthClientSetting, formik: FormikHelpers<OAuthClientSetting>) {
    formik.setSubmitting(true);
    this.props.addOAuthClientSubmit(client, this.props.formik);
    formik.setSubmitting(false);
    this.props.close(true);
  }

  public render() {
    const initialValues = { dbClass: '', instanceUrl: '', oauthClientId: '', oauthClientSecret: '', oauthClientRedirectUri: '' };
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={(values: OAuthClientSetting, formik: FormikHelpers<OAuthClientSetting>) => {
          this.handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<AddOAuthClientDialogState>) => (
          <form onSubmit={formik.handleSubmit}>
            <div className='tb-dialog-title'>{Messages.oauthClientDialogTitle()}</div>
            <table className='tb-form-table'>
              <tbody>
                <tr>
                  <td className='tb-text-input-label-cell'>
                    <label htmlFor='dbClass'>{Messages.oauthClientDbClass()}</label>
                  </td>
                  <td className='tb-settings-control tb-input-wide'>
                    <FormikTextBox
                      name='dbClass'
                      testId='dbClass'
                      label=''
                      validate={this.validateNoEmbeddedWhiteSpace}
                      className={textFieldWidget}
                      showValidationMessage={true}
                    />
                  </td>
                </tr>
                <tr>
                  <td className='tb-text-input-label-cell'>
                    <label htmlFor='instanceUrl'>{Messages.oauthClientInstanceUrl()}</label>
                  </td>
                  <td className='tb-settings-control tb-input-wide'>
                    <FormikTextBox
                      name='instanceUrl'
                      testId='instanceUrl'
                      label=''
                      validate={this.validateInstanceUrl}
                      className={textFieldWidget}
                      showValidationMessage={true}
                      placeholder='Optional'
                    />
                  </td>
                </tr>
                <tr>
                  <td className='tb-text-input-label-cell'>
                    <label htmlFor='oauthClientId'>{Messages.oauthClientId()}</label>
                  </td>
                  <td className='tb-settings-control tb-input-wide'>
                    <FormikTextBox
                      name='oauthClientId'
                      testId='oauthClientId'
                      label=''
                      validate={this.validateNoEmbeddedWhiteSpace}
                      className={textFieldWidget}
                      showValidationMessage={true}
                    />
                  </td>
                </tr>
                <tr>
                  <td className='tb-text-input-label-cell'>
                    <label htmlFor='oauthClientSecret'>{Messages.oauthClientSecret()}</label>
                  </td>
                  <td className='tb-settings-control tb-input-wide'>
                    <FormikTextBox
                      name='oauthClientSecret'
                      testId='oauthClientSecret'
                      label=''
                      validate={this.validateNoEmbeddedWhiteSpace}
                      className={textFieldWidget}
                      showValidationMessage={true}
                    />
                  </td>
                </tr>
                <tr>
                  <td className='tb-text-input-label-cell'>
                    <label htmlFor='oauthClientRedirectUri'>{Messages.oauthRedirectUri()}</label>
                  </td>
                  <td className='tb-settings-control tb-input-wide'>
                    <FormikTextBox
                      name='oauthClientRedirectUri'
                      testId='oauthClientRedirectUri'
                      label=''
                      validate={this.validateRedirectUri}
                      className={textFieldWidget}
                      showValidationMessage={true}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <div className='tb-dialog-actions'>
              <ButtonWidget buttonType={ButtonType.Outline}
                testId='add-oauth-client-dialog-dismiss'
                onClick={() => this.props.dismiss()}
                disabled={formik.isSubmitting}
                type='button'>{Messages.dismiss()}
              </ButtonWidget>
              <ButtonWidget buttonType={ButtonType.Primary}
                testId={'add-oauth-client-dialog-add-oauth-client-button'}
                className='tb-margin-left-wide'
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
                onFocus={() => true}
                type='submit'>{Messages.addOAuthClient_action()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}
